import { useState } from "react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Sparkles, Loader2, Copy, CheckCircle2, Save, ArrowRight, Rocket } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

/**
 * Reusable AI Assistant button. Drops into any page.
 *
 * Props:
 *  - endpoint (string) — e.g. "/ai-copilot/risk/suggest-mitigation"
 *  - payload  (object | function) — either a fixed object, or a fn that returns the body on click
 *  - label    (string) — button label (default "Ask AI")
 *  - title    (string) — dialog title
 *  - subtitle (string) — dialog description
 *  - renderResult (function({data, source}) => jsx)  — custom result renderer
 *  - variant  (string) — button variant
 *  - size     (string) — button size
 *  - className (string)
 *  - testId   (string)
 */
export default function AIAssistButton({
  endpoint,
  payload,
  label = "Ask AI",
  title = "AI Assistant",
  subtitle = "",
  renderResult,
  variant = "outline",
  size = "sm",
  className = "border-purple-300 text-purple-700 hover:bg-purple-50",
  testId = "ai-assist-btn",
  toolKey = null,              // enables Save-to-Workspace when set
  saveTitle = "",              // optional pre-filled title for the saved draft
}) {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedInfo, setSavedInfo] = useState(null); // { id, route_hint }
  const [promoting, setPromoting] = useState(false);
  const [promotedInfo, setPromotedInfo] = useState(null); // {count, route_hint}

  const run = async () => {
    setLoading(true);
    setResult(null);
    setSavedInfo(null);
    setPromotedInfo(null);
    try {
      const body = typeof payload === "function" ? payload() : payload;
      const res = await api.post(endpoint, body || {});
      if (res.data?.success) {
        setResult(res.data);
        const src = res.data.source === "gpt-4o" ? "AI" : "Fallback";
        toast.success(`AI response ready (${src})`);
      } else {
        toast.error("AI returned an error");
      }
    } catch (e) {
      toast.error("AI call failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  const saveToWorkspace = async () => {
    if (!toolKey || !result?.data) return;
    setSaving(true);
    try {
      const res = await api.post("/ai-copilot/save-to-workspace", {
        tool_key: toolKey,
        title: saveTitle,
        data: result.data,
      });
      if (res.data?.success) {
        setSavedInfo({ id: res.data.id, route_hint: res.data.route_hint });
        toast.success("Saved as draft to your workspace");
      } else {
        toast.error(res.data?.error || "Save failed");
      }
    } catch (e) {
      toast.error("Save failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const goToModule = () => {
    const route = promotedInfo?.route_hint || savedInfo?.route_hint;
    if (route) {
      setOpen(false);
      navigate(route);
    }
  };

  const promote = async () => {
    if (!savedInfo?.id) return;
    setPromoting(true);
    try {
      const res = await api.post(`/ai-copilot/drafts/${savedInfo.id}/promote`);
      if (res.data?.success) {
        setPromotedInfo({ count: res.data.count || 1, route_hint: res.data.route_hint });
        toast.success(`${res.data.message}`);
      } else {
        toast.error(res.data?.error || "Promote failed");
      }
    } catch (e) {
      toast.error("Promote failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setPromoting(false);
    }
  };

  const handleOpen = async () => {
    setOpen(true);
    await run();
  };

  const copyJson = () => {
    navigator.clipboard.writeText(JSON.stringify(result?.data || {}, null, 2));
    toast.success("Copied to clipboard");
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={handleOpen}
        className={`gap-2 ${className}`}
        data-testid={testId}
      >
        <Sparkles className="w-4 h-4" />
        {label}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid={`${testId}-dialog`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              {title}
              {result && (
                <Badge className="bg-purple-100 text-purple-700 gap-1 ml-2">
                  <Sparkles className="w-3 h-3" />
                  {result.source === "gpt-4o" ? "AI" : "Fallback"}
                </Badge>
              )}
            </DialogTitle>
            {subtitle && <DialogDescription>{subtitle}</DialogDescription>}
          </DialogHeader>

          {loading && (
            <div className="flex items-center justify-center py-14 gap-2 text-slate-600">
              <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
              <span>AI is thinking…</span>
            </div>
          )}

          {!loading && result && (
            <div className="space-y-3">
              {renderResult
                ? renderResult({ data: result.data, source: result.source })
                : <DefaultResultView data={result.data} />}
            </div>
          )}

          <DialogFooter className="gap-2 flex-wrap">
            <Button variant="outline" onClick={copyJson} disabled={!result} className="gap-2">
              <Copy className="w-4 h-4" /> Copy JSON
            </Button>
            <Button onClick={run} disabled={loading} className="bg-purple-600 hover:bg-purple-700 gap-2">
              <Sparkles className="w-4 h-4" />
              Regenerate
            </Button>
            {toolKey && result && !savedInfo && (
              <Button
                onClick={saveToWorkspace}
                disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                data-testid={`${testId}-save`}
              >
                {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Save className="w-4 h-4" /> Save to Workspace</>}
              </Button>
            )}
            {savedInfo && !promotedInfo && (
              <Button
                onClick={promote}
                disabled={promoting}
                className="bg-indigo-600 hover:bg-indigo-700 text-white gap-2"
                data-testid={`${testId}-promote`}
              >
                {promoting
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Promoting…</>
                  : <><Rocket className="w-4 h-4" /> Promote to Live</>}
              </Button>
            )}
            {promotedInfo && (
              <Badge className="bg-emerald-100 text-emerald-700 gap-1 px-2 py-1 text-xs">
                <CheckCircle2 className="w-3 h-3" /> {promotedInfo.count} live record(s) created
              </Badge>
            )}
            {(savedInfo || promotedInfo) && (
              <Button
                onClick={goToModule}
                className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                data-testid={`${testId}-goto`}
              >
                Open in Module <ArrowRight className="w-4 h-4" />
              </Button>
            )}
            <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Generic renderer: walks a JSON object and produces clean cards/lists.
function DefaultResultView({ data }) {
  if (!data || typeof data !== "object") return null;
  return (
    <div className="space-y-3">
      {Object.entries(data).map(([key, value]) => (
        <div key={key} className="rounded-lg border border-slate-200 bg-white p-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
            {key.replace(/_/g, " ")}
          </div>
          <JsonValue value={value} />
        </div>
      ))}
    </div>
  );
}

function JsonValue({ value }) {
  if (value === null || value === undefined) {
    return <span className="text-slate-400">—</span>;
  }
  if (typeof value === "string") {
    return <p className="text-sm text-slate-700 whitespace-pre-wrap">{value}</p>;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return <span className="text-sm font-semibold text-slate-900">{String(value)}</span>;
  }
  if (Array.isArray(value)) {
    return (
      <ul className="space-y-1.5">
        {value.map((v, i) => (
          <li key={i} className="text-sm text-slate-700">
            {typeof v === "object" && v !== null ? (
              <div className="rounded bg-slate-50 border border-slate-200 p-2">
                {Object.entries(v).map(([k, vv]) => (
                  <div key={k} className="flex gap-2 text-xs">
                    <span className="font-medium text-slate-600 min-w-[120px]">{k.replace(/_/g, " ")}:</span>
                    <span className="text-slate-800">
                      {Array.isArray(vv) ? vv.join(", ")
                        : (typeof vv === "object" ? JSON.stringify(vv) : String(vv))}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <>
                <CheckCircle2 className="inline w-3 h-3 mr-1 text-emerald-500" />
                {String(v)}
              </>
            )}
          </li>
        ))}
      </ul>
    );
  }
  // object
  return (
    <div className="rounded bg-slate-50 border border-slate-200 p-2 space-y-1">
      {Object.entries(value).map(([k, v]) => (
        <div key={k} className="flex gap-2 text-xs">
          <span className="font-medium text-slate-600 min-w-[120px]">{k.replace(/_/g, " ")}:</span>
          <span className="text-slate-800">
            {typeof v === "object" && v !== null ? JSON.stringify(v) : String(v)}
          </span>
        </div>
      ))}
    </div>
  );
}
